var debug = console.log;
const CONSTANTS = require('@/constants');
const Bugsnag = require("@bugsnag/js");
const eventBus = require('@/eventBus').default || require('@/eventBus');

function dbmanager() {
    if (!this.browserSupportCheck()) {
        debug("BROWSER NOT SUPPORTED!");
        return;
    }
    this.autosave = true;
    this.eventqueue = [];
    this.eventqueuemid = [];
    this.eventqueuehigh = [];
    this.isSyncDone = true;
    this.syncRetryCount = 0;
    this.syncPausedNotified = false;
    // JAVÍTÁS: lásd a sync()/syncDone() kommentjeit - ez a Map tartja számon,
    // mely UUID-k vannak épp "repülőben" (kiküldve, de még nem visszaigazolva),
    // MIKOR küldtük ki őket, és MELYIK verziót (clientTime) - hogy egy már
    // folyamatban lévő, VÁLTOZATLAN rekordot ne küldjünk ki újra minden egyes
    // beérkező storedone-nál, de egy KÖZBEN történő ÚJABB szerkesztést (eltérő
    // clientTime) mindig, feltétel nélkül kiküldjünk, még akkor is, ha az
    // előző verzió visszaigazolása még nem érkezett meg. A `clientTime`
    // egyeztetés nélkül (pusztán UUID alapján) egy gyors egymás utáni dupla
    // szerkesztés második verziója CSENDBEN elveszhetett volna: a syncDone()
    // a storedone megérkezésekor mindig a HELYI (esetleg már újabb) rekordot
    // jelöli szinkronizáltnak, tekintet nélkül arra, hogy pontosan MELYIK
    // verziót nyugtázta a szerver - ha a második verzió sosem ment volna ki
    // külön, a kliens tévesen szinkronizáltnak hitte volna, holott a szerver
    // sosem kapta meg. A timestamp emellett biztonsági háló is: ha egy
    // storedone valamiért (ritka, pl. socket.io belső ack-vesztés disconnect
    // nélkül) sosem érkezik meg, a rekord ne ragadjon örökre "repülőben"
    // állapotban, hanem egy idő után újra próbálkozzon.
    this.pendingSyncUUIDs = new Map();
    // JAVÍTÁS (ÍRÁS-VIHAR, 32021440528): UUID -> az a `clientTime`, amelyet a
    // syncDone() a szerver visszaigazolása alapján MÁR szinkronizáltnak
    // jelölt. A sync() egy ilyen, pontosan ezzel a `clientTime`-mal szereplő
    // rekordot nem küld ki újra akkor sem, ha a `readAll()` pillanatképe még a
    // jelölés ELŐTTI (synced:false) állapotot mutatja. Ez a pillanatkép
    // ugyanis elavult is lehet: a workerben az olvasások NINCSENEK sorba
    // állítva az írásokkal, a fő szál pedig terhelés alatt jóval később
    // dolgozza fel az eredményt, mint ahogy a worker elkészítette. Egy
    // tényleges szerkesztés mindig új `clientTime`-ot kap (l. `_update`),
    // arra ez a szűrő soha nem vonatkozik.
    this.confirmedVersions = new Map();

    try {
        this.worker = new Worker('./db.worker.js');
    } catch (e){
        console.log("Hiba a worker létrehozása közben", e);
    }
    this.pendingRequests = new Map();

    this.worker.onmessage = ({ data }) => {
        if (this.pendingRequests.has(data.id)) {
            const { resolve, reject } = this.pendingRequests.get(data.id);
            if (data.error) {
                reject(new Error(data.error));
            } else {
                resolve(data.payload);
            }
            this.pendingRequests.delete(data.id);
        }
    };

    this.worker.onerror = (error) => {
        console.error('Error in DB Worker:', error);
        Bugsnag.notify(error);
    };
}

dbmanager.prototype._postMessageToWorker = function (action, params) {
    return new Promise((resolve, reject) => {
        const id = this.generateUUID();
        this.pendingRequests.set(id, { resolve, reject });
        this.worker.postMessage({ id, action, params });
    });
};

dbmanager.prototype.cleanIfSynced = function () {
    return this._postMessageToWorker('cleanIfSynced');
};
dbmanager.prototype.isSynced = function () {
    return this._postMessageToWorker('isSynced');
};
dbmanager.prototype._readall = function (table) {
    if (typeof table === 'undefined') {
        console.trace();
        return Promise.reject('Table name is undefined for _readall');
    }
    return this._postMessageToWorker('readall', { table });
};

dbmanager.prototype._read = function (table, key) {
    return this._postMessageToWorker('read', { table, key });
};

// JAVÍTÁS (ÍRÁS-VIHAR, 32021440528): szigorúan növekvő `clientTime`. A teljes
// szinkron-védelem (pendingSyncUUIDs, confirmedVersions, syncDone
// isStillSameVersion) arra épül, hogy KÉT KÜLÖNBÖZŐ tartalmú verzió soha nem
// kapja ugyanazt a `clientTime`-ot. A `ts.now()` ezt nem garantálja: ha a fő
// szál lemarad, és két mentés ugyanabban a milliszekundumban fut le, mindkettő
// azonos bélyeget kap - a szerver visszaigazolása ilyenkor mindkettőt
// "igazolja", és a később mentett tartalom sosem megy ki. Ha a pillanatnyi
// idő nem nagyobb az utoljára kiadottnál, 0,01 ms-mal (10 µs) megnöveljük -
// ez az idővonal rendezésében láthatatlan (az mp-es pontosságú játékidő és a
// `date` mező dönt), az 1,79e12 nagyságrendű lebegőpontos számon viszont
// biztosan megkülönböztethető (a legkisebb lépés ott ~0,00024).
dbmanager.prototype._nextClientTime = function () {
    const now = window.ts ? window.ts.now() : Date.now();
    const next = (typeof this._lastClientTime === 'number' && now <= this._lastClientTime)
        ? this._lastClientTime + 0.01
        : now;
    this._lastClientTime = next;
    return next;
};

// JAVÍTÁS (79. tétel): `options.period` - ha meg van adva, az esemény ebbe a
// periódusba kerül a jelenlegi (window.period) helyett. A worker a `period`
// paraméterrel MINDIG felülírja a rekord saját `period` mezőjét, ezért ez
// kifejezett, opcionális paraméter: a többi hívó viselkedése nem változik.
// Jelenleg csak a CR ActionBar használja (a "Mérkőzés vége" / "Félidő" alatt
// rögzített esemény az utolsó lejátszott félidőbe kerüljön).
dbmanager.prototype._write = function (value, nosync, options) {
    const cleanValue = JSON.parse(JSON.stringify(value));
    const period = (options && options.period !== undefined && options.period !== null) ? String(options.period) : window.period;

    const promise = this._postMessageToWorker('write', {
        value: cleanValue,
        dbname: window.dbname,
        period: period,
        usertype: window.usertype,
        clientTime: this._nextClientTime()
    });

    promise.then(() => {
        eventBus.$emit(CONSTANTS.EVENTS.DB_CHANGE);
    });

    return promise;
};

dbmanager.prototype._update = function (value, dontsync) {
    const cleanValue = JSON.parse(JSON.stringify(value));

    // JAVÍTÁS: a sync()/syncDone() `clientTime`-alapú verzió-egyeztetése (l.
    // a 34. tétel kommentjeit fentebb) azon az alapfeltevésen áll, hogy MINDEN
    // valódi, szinkronizálandó helyi módosítás (dontsync hamis/hiányzik) egy
    // ÚJ clientTime-ot kap. Ez korábban NEM volt garantálva központilag - pl.
    // a `TimelineList.vue` `deleteItem`-je és a `dbmanager.remove()` (ami a
    // `DmrScreen.vue`-ból hívódik) a MEGLÉVŐ (törlés előtti) clientTime-mal
    // hívta az update()-et. Ha egy rekordot gyorsan szerkesztettek, majd - MÉG
    // a szerkesztés visszaigazolása ELŐTT - töröltek, a törlés clientTime-ja
    // megegyezett volna a "repülőben" lévő szerkesztés clientTime-jával, és a
    // syncDone() a szerkesztés visszaigazolásakor TÉVESEN a törlést is
    // szinkronizáltnak jelölte volna, miközben a törlés ténye sosem jutott
    // volna el a szerverre - csendes adatvesztés (egy nem törölhető esemény).
    // Ahelyett hogy minden jelenlegi ÉS jövőbeli hívó helyet egyenként kellene
    // erre emlékeztetni, itt, az EGYETLEN choke pointon (minden `update()`
    // hívás ezen megy át) stamppeljük a friss clientTime-ot minden genuine
    // (nem dontsync) módosításnál. Ha a hívó már amúgy is beállított egy
    // frisset (pl. ActionBar.vue), ez ártalmatlanul egy még frissebbre cseréli.
    if (!dontsync) {
        cleanValue.clientTime = this._nextClientTime();
    }

    const promise = this._postMessageToWorker('update', {
        value: cleanValue,
        dontsync,
        dbname: window.dbname,
        usertype: window.usertype
    });

    promise.then(() => {
        eventBus.$emit(CONSTANTS.EVENTS.DB_CHANGE);
    });

    return promise;
};

dbmanager.prototype.syncDone = function (data) {
    eventBus.$emit(CONSTANTS.EVENTS.CHANGE.CONNECTION, 'okfromsync');
    if (typeof data === 'undefined'||data == null) {
        return;
    }
    var self = this;
    // JAVÍTÁS: mielőtt töröljük, megnézzük, MELYIK verziót (clientTime)
    // igazolta vissza ez a storedone - a `data` (a storedone payloadja)
    // csak a UUID-t tartalmazza, verzióinfót nem, ezt a sync()-ben elmentett
    // pendingSyncUUIDs bejegyzésből tudjuk visszanyerni.
    const pendingEntry = self.pendingSyncUUIDs.get(data);
    // JAVÍTÁS (ÍRÁS-VIHAR, 32021440528): a bejegyzést itt korábban azonnal
    // töröltük, a rekordot viszont csak két worker-körrel később (`_read`,
    // majd `update`) jelöltük szinkronizáltnak. Ebben a résben a rekord
    // "synced:false, és nincs repülőben" állapotúnak látszott, így minden
    // közben lefutó sync() - és minden egyes storedone indít egyet - UGYANAZT
    // a verziót újra kiküldte. Az újabb küldés újabb storedone-t hozott,
    // az újabb sync()-et: terhelés alatt (amikor a fő szál lemarad, és a rés
    // kitágul) ez önfenntartó hurokká vált. Élesben 194 331 írás lett 491
    // valódi verzióból, 24 percen át.
    // Most a bejegyzés a döntésig "visszaigazolt" jelzéssel megmarad, és a
    // sync() addig nem küldi újra ezt a verziót. Egy ÚJABB verzió (más
    // `clientTime`) továbbra is azonnal kimegy - ezen a jelzés nem változtat.
    if (pendingEntry) {
        pendingEntry.acked = true;
    }
    // Csak akkor töröljük, ha közben nem került a helyére egy újabb küldés
    // bejegyzése (az már egy másik, még nem visszaigazolt verzióé).
    const releasePending = () => {
        if (self.pendingSyncUUIDs.get(data) === pendingEntry) {
            self.pendingSyncUUIDs.delete(data);
        }
    };
    this._read(window.dbname, data).then((dataobject) => {
        // a storedone UUID-ja innentől nincs "repülőben" - a sync() legközelebbi
        // hívásában újra elküldhető, HA időközben (pl. egy másik szerkesztés
        // miatt) újra unsynced állapotba került.
        releasePending();
        if (dataobject) {
            // JAVÍTÁS: ha a HELYI rekordot (a fenti `_read` szerint) MÁR EGY
            // ÚJABB szerkesztés érte, mint amit erre a UUID-ra utoljára
            // kiküldtünk (eltérő clientTime), akkor ez a storedone CSAK a
            // RÉGEBBI verziót igazolja vissza - az újabbat NEM szabad
            // szinkronizáltnak jelölni, különben a tartalma soha nem jutna el
            // a szerverhez (csendes adatvesztés). Ilyenkor a rekordot
            // unsynced állapotban hagyjuk, és az azonnal következő sync()
            // hívás (lásd lent) - mivel a pendingSyncUUIDs bejegyzést már
            // fentebb töröltük - garantáltan újraküldi a friss verziót.
            const isStillSameVersion = !pendingEntry || pendingEntry.clientTime === dataobject.clientTime;
            if (isStillSameVersion) {
                dataobject.synced = true;
                dataobject.syncDone = true;
                // Ugyanabban a szinkron lépésben, mint a fenti releasePending(),
                // hogy ne maradjon rés a két védelem között (l. confirmedVersions).
                if (typeof dataobject.clientTime === 'number') {
                    self.confirmedVersions.set(data, dataobject.clientTime);
                }
                console.log(`[SYNCDONE] Marking record synced. ID: ${dataobject.UUID || dataobject.id} | action: ${dataobject.action} | clientTime: ${dataobject.clientTime} | partialUpdate: ${dataobject.partialUpdate}`);
                if (window.vuexStore) {
                    window.vuexStore.commit('game/UPDATE_TIMELINE_EVENT', dataobject);
                }
                self.update(dataobject, true).then(()=>{
                    self.isSyncDone = true;
                    self.sync();
                }, () => {
                    // Ha a jelölés nem került be a helyi adatbázisba, a rekord
                    // ott synced:false marad - ilyenkor engedjük újraküldeni,
                    // hogy a következő visszaigazolás újra megpróbálja
                    // megjelölni (ugyanaz a viselkedés, mint e javítás előtt).
                    if (self.confirmedVersions.get(data) === dataobject.clientTime) {
                        self.confirmedVersions.delete(data);
                    }
                    self.isSyncDone = true;
                    self.sync();
                });
            } else {
                console.log(`[SYNCDONE] Skipping synced flag - newer local edit exists. ID: ${dataobject.UUID || dataobject.id} | acked clientTime: ${pendingEntry.clientTime} | current clientTime: ${dataobject.clientTime}`);
                self.isSyncDone = true;
                self.sync();
            }
        } else {
             self.isSyncDone = true;
             self.sync();
        }
    }, () => {
        // Sikertelen olvasásnál a bejegyzést el kell engedni, különben a
        // "visszaigazolt" jelzés végleg blokkolná ennek a verziónak az
        // újraküldését.
        releasePending();
        self.isSyncDone = true;
        self.sync();
    });
};

dbmanager.prototype.getinitData = function () {
    var self = this;

    return new Promise(
        function (resolve) {
            let getPeriodDuration = (period, periodLength) => {
                let periodCalculator = {
                    extended: 5 * 60,
                    normal: periodLength * 60,
                };
                return periodCalculator[period.type] || 0;
            };

            self.readAll().then((data) => {
                let _gamedata = {};

                data.forEach(row => {
                    if (row.action === 'init') {
                        let team;
                        let callback = (person) => {
                            person.cards = {
                                yellow: { value: false, given: undefined },
                                red: { value: false, given: undefined },
                                blue: { value: false, given: undefined }
                            };
                            person.penalties = [];
                            person.strandYellows = [];
                            person.disabled = require('@/DefaultBlockList')();
                            person.team = team;
                        };
                        team = 'teamA';
                        if(Array.isArray(row.params.team_a_players)&&Array.isArray(row.params.team_a_officials)){
                            [...row.params.team_a_players, ...row.params.team_a_officials].forEach(callback);
                        }
                        team = 'teamB';
                        if(Array.isArray(row.params.team_b_players)&&Array.isArray(row.params.team_b_officials)){
                            [...row.params.team_b_players, ...row.params.team_b_officials].forEach(callback);
                        }
                        if(Array.isArray(row.params.team_a_officials)&&Array.isArray(row.params.team_b_officials)){
                            [...row.params.team_a_officials, ...row.params.team_b_officials].forEach((person) => {
                                person.official = true;
                                person.maxPenalties = 3;
                            });
                        }
                        if(Array.isArray(row.params.team_a_players)&&Array.isArray(row.params.team_b_players)){
                            [...row.params.team_a_players, ...row.params.team_b_players].forEach((person) => {
                                person.official = false;
                                person.maxPenalties = 3;
                            });
                        }
                        if(Array.isArray(row.params.periods)) {
                            let previousPeriod = null;

                            // Sort periods chronologically before computing startat
                            let isStrand = row.params.rule === 'STRAND';
                            row.params.periods.sort((a, b) => {
                                const getChronoOrder = require('@/utils/periodOrder').getChronoOrder;
                                return getChronoOrder(a.value, isStrand) - getChronoOrder(b.value, isStrand);
                            });

                            row.params.periods.forEach((period) => {
                                period.duration = getPeriodDuration(period, row.params.period_length);
                                period.isDisabled = period.value === '14';
                                period.order = period.value === '13' ? 14 : period.value === '14' ? 13 : period.value;
                                period.previousValue = previousPeriod ? previousPeriod.value : null;
                                previousPeriod = period;
                            });

                            // A `startat` számítása a KÖZÖS `periodStartat.js`-ben
                            // történik - l. az ottani magyarázatot arról, miért nem
                            // szabad ezt a logikát több példányban implementálni.
                            const { applyPeriodStartAt, collectReachedPeriods } = require('@/utils/periodStartat');
                            applyPeriodStartAt(
                                row.params.periods,
                                collectReachedPeriods(data),
                                window.period
                            );
                        }
                        _gamedata = row;
                    }
                });
                if (Object.keys(_gamedata).length === 0) {
                    return resolve(null);
                }
                resolve(JSON.parse(JSON.stringify(_gamedata)));
            }).catch((error) => {
                // BIZTONSÁGI HÁLÓ: e nélkül egy itteni dobás (vagy a readAll()
                // elutasítása) esetén a promise SOHA nem dőlne el, a hívó pedig
                // örökre várna rá. A `gamedataCallback()` épp ezt a függvényt várja
                // meg, MIELŐTT kiírná az `init` rekordot - egy beragadás tehát azt
                // okozná, hogy az `init` sosem jön létre, és onnantól a `gamedata()`
                // is minden hívásnál elhasalna. A `null` a hívók számára már ma is
                // kezelt, ismert kimenet (l. gamedataCallback: `gamedata == null`).
                console.error("getinitData failed:", error);
                Bugsnag.notify(error instanceof Error ? error : new Error("getinitData failed: " + error), function (event) {
                    event.severity = 'error';
                    event.context = 'dbmanager.getinitData()';
                    event.addMetadata('match', { merkozesId: window.dbname, usertype: window.usertype });
                });
                resolve(null);
            });
        }
    );
};

dbmanager.prototype.gamedataCallback = function (data) {
    let self = this;
    return new Promise(
        function (resolve) {
            self.getinitData().then(gamedata => {
                debug("GAMEDATA", gamedata);
                if (gamedata == null && data) {
                    let init = {};
                    // JAVÍTÁS (80. tétel - 32021426449): az 'init' rekord meccsenként
                    // ÁLLANDÓ azonosítót kap. Korábban minden eszköz (CR, DMR, SUB) az
                    // első belépésekor saját, véletlen UUID-dal írt egy 'init'-et, így
                    // egy meccshez több is keletkezett, és a DMR - amelyik élőben
                    // megkapta a CR-ét is - minden játékost kétszer listázott. A szerver
                    // a rekordokat UUID szerint upserteli (egyedi indexszel), tehát az
                    // azonos UUID-ú második 'init' ugyanazt a dokumentumot frissíti.
                    // Meccsazonosító hiányában marad a véletlen UUID (a write() adja).
                    if (data.id !== undefined && data.id !== null && String(data.id) !== '') {
                        init.UUID = 'init-' + String(data.id);
                    }
                    init.action = "init";
                    init.params = data;
                    self.write(init);
                } else if (data && gamedata && gamedata.params && data.id != gamedata.params.id) {
                    debug("új szobában vagyunk!");
                    self._postMessageToWorker('clear');
                } else if (data && gamedata && gamedata.params && data.id == gamedata.params.id) {
                    // JAVÍTÁS: ugyanabba a szobába (meccsbe) lépünk vissza - a helyben
                    // tárolt "init" rekord a LEGELSŐ csatlakozáskori állapotot
                    // fagyasztotta be (pl. extra_time_1/2, periods), és korábban SOHA
                    // nem frissült, még akkor sem, ha a meccs beállításai időközben
                    // megváltoztak a szerveren (pl. utólag engedélyezték a
                    // hosszabbítást). Emiatt a gamedata.periods (amit a Stopwatch/óra
                    // a periódusok kezdő idejének kiszámolásához használ) véglegesen
                    // elavult adaton alapult, miközben a bejelentkezéskor frissen
                    // kapott `data` már a szerver aktuális állapotát tartalmazza -
                    // ezzel frissítjük rá a helyi "init" rekordot minden belépéskor.
                    self.update({UUID: gamedata.UUID, action: 'init', params: data}, true);
                }
                resolve();
            });
        }
    );
};

dbmanager.prototype.createRoom = function () {
    return this._postMessageToWorker('createRoom', {dbname: window.dbname});
};

// JAVÍTÁS (4514 - proaktív felszabadítás): szándékosan NEM a
// `_postMessageToWorker`-en (ami egy `id`-t generálna és egy pending
// Promise-t regisztrálna, választ várva) keresztül megy - ezt a lap
// elhagyásakor (pagehide) hívjuk, ahol NEM akarunk/nem is tudunk megbízhatóan
// megvárni egy választ, csak a lehető leggyorsabban jelezni a workernek,
// hogy zárja be a kapcsolatot. Egy `id` nélküli üzenetre a worker válasza
// (ha egyáltalán visszaküldi) a fő szál `onmessage`-jében ártalmatlanul
// eldobásra kerül (l. dbmanager.js onmessage: `pendingRequests.has(data.id)`).
dbmanager.prototype.closeConnection = function () {
    try {
        if (this.worker) {
            this.worker.postMessage({action: 'closeDb'});
        }
    } catch (e) { /* ignore - a lap úgyis eltűnik */ }
};

dbmanager.prototype.clear = function () {
    return this._postMessageToWorker('clear', {dbname: window.dbname});
};

dbmanager.prototype.initdb = function (dbname) {
    var self = this;

    window.vueio.on(CONSTANTS.SOCKET.ACTIONS.CONNECT, data => {
        eventBus.$emit(CONSTANTS.EVENTS.CHANGE.CONNECTION, 'ok');
        this.isSyncDone = true;
        self.sync();
        self.syncDone(data);
    });

    window.vueio.on(CONSTANTS.SOCKET.ACTIONS.DISCONNECT, () => {
        eventBus.$emit(CONSTANTS.EVENTS.CHANGE.CONNECTION, 'error');
        // JAVÍTÁS (ÍRÁS-VIHAR): itt korábban `self.pendingSyncUUIDs.clear()` állt.
        // A szándék helyes volt - lecsatlakozáskor a "repülőben" lévő store-ok
        // sorsa bizonytalan, ezért a következő sync()-nek újra el kell tudnia
        // küldeni őket -, DE a megvalósítás egy újracsatlakozás-viharban
        // teljesen kiütötte a duplikáció-védelmet: MINDEN lecsatlakozás
        // kiürítette a táblát, a rá következő csatlakozás pedig az ÖSSZES még
        // vissza nem igazolt rekordot újraküldte. Így a védelemnek soha nem
        // volt esélye életbe lépni.
        //
        // Valós eset (32021428634, 2026-09-23): a socket oldali Bugsnagben
        // 2 886 bejelentkezés ugyanarra a mérkőzésre, az adatban 30 780 írás
        // 1 328 valódi verzióra (95,7% felesleges), csúcson 16 331 írás EGYETLEN
        // perc alatt, egy rekordon 1 924 azonos `clientTime`-mal. A gép a
        // LEGFRISSEBB buildet futtatta (Bugsnag app.version: 2.1.20260921134707),
        // tehát a 34. tétel védelme ott volt a kódban - csak ez a `clear()`
        // semlegesítette. Az áradat blokkolta a fő szálat, amitől a socket
        // ping-timeoutba futott, az újabb lecsatlakozás pedig újra ürítette a
        // táblát: önerősítő hurok.
        //
        // MIÉRT NEM KELL A TÖRLÉS: a `sync()` amúgy is újraküld mindent, aminek
        // a bejegyzése `SYNC_PENDING_STALE_MS`-nél (15 mp) régebbi - pontosan ez
        // az elévülés lett annak idején azért beépítve, hogy egy elveszett
        // visszaigazolás ne ragassza be a rekordot örökre. A törlés tehát
        // felesleges volt: a hosszabb kiesés után (>15 mp) minden újramegy
        // magától, egy rövid megszakadás után pedig épp azt NEM akarjuk, hogy
        // azonnal újraküldjük azt, ami már lehet, hogy megérkezett.
        //
        // ADATBIZTONSÁG: a dedup a `clientTime`-ot is nézi. Ha a rekordot a
        // kiesés alatt SZERKESZTETTÉK, más lesz a `clientTime`, a bejegyzés nem
        // egyezik, és a rekord AZONNAL újramegy - a késleltetés tehát kizárólag
        // a változatlan, már egyszer elküldött rekordokat érinti.
    });

    let x = false;

    eventBus.$on('LOADQUEUE', () => {
        while (self.eventqueuemid.length) {
            let element = self.eventqueuemid.pop();
            eventBus.$emit(element.event, element.eventdata);
        }
        while (self.eventqueue.length) {
            let element = self.eventqueue.pop();
            eventBus.$emit(element.event, element.eventdata);
        }
    });

    eventBus.$on(CONSTANTS.EVENTS.CHANGE.GAMEDATALOADED, () => {
        if (x) {
            while (self.eventqueuemid.length) {
                let element = self.eventqueuemid.pop();
                eventBus.$emit(element.event, element.eventdata);
            }
            while (self.eventqueue.length) {
                let element = self.eventqueue.pop();
                eventBus.$emit(element.event, element.eventdata);
            }
        }
        x = true;
        while (self.eventqueuehigh.length) {
            let element = self.eventqueuehigh.pop();
            debug("GDLoaded EVENT futás: ",element.event, element.eventdata);
            eventBus.$emit(element.event, element.eventdata);
        }
    });

    window.vueio.on(CONSTANTS.SOCKET.ACTIONS.STOREDONE, data => {
        self.syncDone(data);
    });

    window.vueio.on(CONSTANTS.SOCKET.ACTIONS.MONGOERROR, data => {
        if(data!=null){
            //console.log(data);
        }
        eventBus.$emit(CONSTANTS.EVENTS.CHANGE.CONNECTION, 'error');
        this.isSyncDone = true;
        self.sync();
    });

    self.dbname = dbname;
    self._startStaleRetry();
    return this._postMessageToWorker('initdb', {dbname: dbname});
};

// JAVÍTÁS (74. tétel): időzített újrapróbálkozás a vissza nem igazolt
// küldésekre. A `SYNC_PENDING_STALE_MS` (15 mp) elévülés eddig csak akkor
// érvényesült, ha VALAMI más elindította a sync()-et (új rögzítés, újabb
// storedone, újracsatlakozás). A CR-en nincs periodikus újraszinkron (az
// App.vue `'report'` feltétele CR-en sosem teljesül, l. 74. tétel), így ha egy
// visszaigazolás elveszett - pl. a kapcsolat épp a küldés után szakadt meg -,
// a rekord a következő felhasználói műveletig szinkronizálatlan maradt. Adat
// nem veszett el, de a lezáráskor (gamefinished) ez fennakadást okozhat.
//
// Ez az időzítő csak a memóriában nézi végig a "repülőben" listát, és kizárólag
// akkor indít sync()-et, ha van ELÉVÜLT és vissza NEM igazolt bejegyzés. A
// visszaigazolt (`acked`) bejegyzéseket nem nézi - azokat a szerver már
// megkapta (l. syncDone). Az `isSyncDone = true` ugyanaz, amit a CONNECT
// kezelő is csinál: enélkül a sync() eleje visszatérne, ha az utolsó küldés
// óta egyetlen visszaigazolás sem érkezett. Egy rekord így legfeljebb 15
// mp-enként megy újra, tehát ez nem tud írás-vihart okozni.
dbmanager.prototype._startStaleRetry = function () {
    const self = this;
    if (this.staleRetryInterval) {
        clearInterval(this.staleRetryInterval);
    }
    this.staleRetryInterval = setInterval(function () {
        if (!window.vueio || !window.vueio.connected) {
            return;
        }
        const now = Date.now();
        let hasStale = false;
        for (const entry of self.pendingSyncUUIDs.values()) {
            if (!entry.acked && (now - entry.sentAt) >= CONSTANTS.SYNC_PENDING_STALE_MS) {
                hasStale = true;
                break;
            }
        }
        if (hasStale) {
            self.isSyncDone = true;
            self.sync();
        }
    }, CONSTANTS.SYNC_STALE_RETRY_CHECK_MS);
};

dbmanager.prototype.browserSupportCheck = function () {
    const indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB;
    const IDBTransaction = window.IDBTransaction || window.webkitIDBTransaction || window.msIDBTransaction;
    const IDBKeyRange = window.IDBKeyRange || window.webkitIDBKeyRange || window.msIDBKeyRange;
    if (!indexedDB || !IDBTransaction || !IDBKeyRange) {
        return false;
    }
    this.indexedDB = indexedDB;
    this.IDBTransaction = IDBTransaction;
    this.IDBKeyRange = IDBKeyRange;
    return true;
};

dbmanager.prototype.write = function (value, nosync, options) {
    return this._write(value, nosync, options);
};

dbmanager.prototype.remove = function (id) {
    // Soft delete: megjelöljük töröltként, hogy a sync elküldhesse a szervernek
    const promise = this.update({id: id, deleted: true, synced: false});
    
    // JAVÍTÁS: A törlés is váltson ki szinkronizációs eseményt.
    promise.then(() => {
        eventBus.$emit(CONSTANTS.EVENTS.DB_CHANGE);
    });
    
    return promise;
};

dbmanager.prototype.read = function (key) {
    return this._read(window.dbname, key);
};
dbmanager.prototype.getSyncableCount = function () {
    return this._postMessageToWorker('getSyncableCount');
};

dbmanager.prototype.readAll = function () {
    return this._readall(window.dbname);
};

dbmanager.prototype.update = function (value, dontsync) {
    return this._update(value, dontsync);
};

dbmanager.prototype.sync = async function () {
    if (!this.isSyncDone) {
        return;
    }
    const self = this;

    if (this.syncRetryCount > 5) {
        console.warn("DB sync paused due to repeated failures.");
        // JAVÍTÁS: ez az állapot korábban NÉMÁN, csak egy console.warn-ban
        // jelent meg - éles környezetben senki nem látja, amíg valaki később
        // észre nem veszi a hiányzó adatokat. Fontos: ez az állapot
        // VISSZAFORDÍTHATATLAN ebben a `dbmanager` példányban - a `sync()`
        // minden további hívása (a 10 másodperces periodikus resync, a
        // DB_CHANGE debounce, a socket connect esemény mind ide vezetnek)
        // itt, idő előtt visszatér, mielőtt esélye lenne a `readAll()`-t
        // újra megpróbálni és a számlálót nullázni - csak egy oldal-
        // újratöltés (ami friss `dbmanager` példányt hoz létre) oldja fel.
        // A `syncPausedNotified` flag biztosítja, hogy csak EGYSZER
        // riasszunk, ne minden egyes (10 másodpercenkénti) sync()-hívásnál.
        if (!this.syncPausedNotified) {
            this.syncPausedNotified = true;
            Bugsnag.notify("DB sync paused due to repeated failures", function (event) {
                event.severity = 'error';
                event.context = 'dbmanager.sync() permanently stalled';
                event.addMetadata('sync', {
                    syncRetryCount: this.syncRetryCount,
                    usertype: window.usertype,
                    merkozesId: window.dbname
                });
            }.bind(this));
        }
        return;
    }

    let dataRead;
    try {
        dataRead = await this.readAll(window.dbname);
        this.syncRetryCount = 0;
    } catch (error) {
        this.syncRetryCount++;
        window.setTimeout(function () {
            debug("sync readAll failed", error);
            self.sync();
        }, 500);
        return;
    }

    if (typeof dataRead !== "undefined" && dataRead !== null) {
        dataRead.forEach(element => {
            if ((typeof element.synced === 'undefined' || !element.synced)) {
                // JAVÍTÁS: ha ez a PONTOSAN EZ A VERZIÓ (UUID + clientTime) már
                // "repülőben" van (kiküldtük, de a szerver storedone-ja még nem
                // érkezett vissza), NE küldjük ki újra. Enélkül minden egyes
                // beérkező storedone egy teljes sync()-et indított (l. syncDone()),
                // ami ÚJRA kiküldte az ÖSSZES még visszaigazolatlan sort is - K db
                // lemaradt sornál ez kb. K²/2 store/storedone üzenetet jelentett
                // (pl. reconnect után), ami éles környezetben ledöntötte a
                // socket-szervert. FONTOS: a `clientTime`-ot IS nézzük, nem csak a
                // UUID-t - ha a rekordot időközben (a régi verzió visszaigazolása
                // előtt) ÚJABB helyi szerkesztés érte (más `clientTime`), azt
                // MINDIG kiküldjük, különben a syncDone() a régi verzió
                // visszaigazolásakor tévesen szinkronizáltnak jelölné az újabb,
                // valójában sosem elküldött tartalmat - csendes adatvesztést okozva.
                const pending = this.pendingSyncUUIDs.get(element.UUID);
                // JAVÍTÁS (ÍRÁS-VIHAR, 32021440528): a visszaigazolt (`acked`)
                // bejegyzés nem évül el - a szerver ezt a verziót már
                // megkapta, csak a helyi jelölés van még folyamatban (l.
                // syncDone). Az elévülés a vissza NEM igazolt küldésekre való.
                if (pending && pending.clientTime === element.clientTime
                    && (pending.acked || (Date.now() - pending.sentAt) < CONSTANTS.SYNC_PENDING_STALE_MS)) {
                    return;
                }
                // JAVÍTÁS (ÍRÁS-VIHAR, 32021440528): ezt a pontos verziót a
                // syncDone() már szinkronizáltnak jelölte, a `readAll()`
                // pillanatképe csak lemaradt róla (l. confirmedVersions). A
                // `typeof` feltétel azért kell, hogy egy `clientTime` nélküli
                // rekord soha ne essen ki véletlenül (undefined === undefined).
                if (typeof element.clientTime === 'number'
                    && this.confirmedVersions.get(element.UUID) === element.clientTime) {
                    return;
                }
                // JAVÍTÁS: ha ez a rekord `partialUpdate` jelzővel lett helyileg
                // elmentve (pl. DMR jóváhagyás/elutasítás, l. DmrScreen.vue
                // saveAndSend), akkor a HELYI példány tartalmi mezői (játékos,
                // terület stb.) elavultak lehetnek - ezt a rekordot itt, az
                // általános újraküldési mechanizmusban is szűkített (csak a
                // jóváhagyás-mezőket tartalmazó) formában kell elküldeni,
                // különben a szerveren tárolt, időközben (pl. CR által)
                // javított tartalmat felülírná a DMR elavult másolata - ugyanaz
                // a hiba, amit a saveAndSend saját, közvetlen küldésénél már
                // kijavítottunk, csak ez a késleltetett/reconnect utáni útvonal.
                let payload = element;
                if (element.partialUpdate === true) {
                    // JAVÍTÁS (2026-08-09, VISSZAVONVA egy korábbi, hibás "védőháló"):
                    // itt korábban volt egy ellenőrzés, ami a `partialUpdate`
                    // jelzőt figyelmen kívül hagyta, ha a `params` a
                    // jóváhagyás-mezőkön túl tartalmi mezőket is tartalmazott
                    // (selectedPlayer, selectedTeam stb.) - abból a feltevésből,
                    // hogy ez csak egy tévesen megjelölt teljes szerkesztés lehet
                    // (l. az akkor talált ActionBar.vue hiba). Ez a feltevés téves
                    // volt: a DMR SAJÁT, NORMÁLIS jóváhagyás/elutasítás rekordja is
                    // MINDIG pontosan így néz ki (a helyi másolat szándékosan
                    // teljes, a DMR saját UI-jához) - a védőháló emiatt a DMR
                    // MINDEN elutasítását/jóváhagyását feltétel nélküli TELJES
                    // íróvá alakította, ami elveszítette a szerver oldali
                    // clientTime-alapú "elavult írás eldobása" védelmet (az
                    // KIZÁRÓLAG a partialUpdate:true ágon fut a szerveren). Ennek
                    // eredményeként egy DMR-oldali, offline közben leadott,
                    // később újraküldött (és emiatt "elavulttá vált") döntés
                    // védelem nélkül felülírhatott egy időközbeni, FRISSEBB CR
                    // javítást. A tényleges gyökérokot (az ActionBar.vue "manualsave"
                    // ága, ami elfelejtette törölni az örökölt `partialUpdate`
                    // jelzőt) már közvetlenül, a forrásnál kijavítottuk - erre a
                    // téves, a normális DMR-működést elrontó extra rétegre nincs
                    // szükség és nem is biztonságos.
                    const p = element.params || {};
                    payload = {
                        UUID: element.UUID,
                        partialUpdate: true,
                        clientTime: element.clientTime,
                        approved: element.approved,
                        rejectReason: element.rejectReason,
                        message: element.message,
                        checked: element.checked,
                        isDraft: element.isDraft,
                        draft: element.draft,
                        params: {
                            approved: p.approved,
                            rejectReason: p.rejectReason,
                            message: p.message,
                            checked: p.checked,
                            isDraft: p.isDraft,
                            draft: p.draft
                        }
                    };
                }
                window.vueio.emit(CONSTANTS.SOCKET.ACTIONS.STORE, payload);
                this.pendingSyncUUIDs.set(element.UUID, {sentAt: Date.now(), clientTime: element.clientTime});
                this.isSyncDone = false;
            }
        });
    }

    if (!this.isSyncDone) {
        eventBus.$emit(CONSTANTS.EVENTS.CHANGE.CONNECTION, 'sync');
    }
};

dbmanager.prototype.gamedata = function () {
    var self = this;

    const addToQueue = function (event, eventdata) {
        self.eventqueue.push({
            event: event,
            eventdata: JSON.parse(JSON.stringify(eventdata))
        });
    };
    const addToMidQueue = function (event, eventdata) {
        self.eventqueuemid.push({
            event: event,
            eventdata: JSON.parse(JSON.stringify(eventdata))
        });
    };
    const addToHighQueue = function (event, eventdata) {
        self.eventqueuehigh.push({
            event: event,
            eventdata: JSON.parse(JSON.stringify(eventdata))
        });
    };

    return new Promise(
        function (resolve) {
            let getPeriodDuration = (period, periodLength) => {
                let periodCalculator = {
                    extended: 5 * 60,
                    normal: periodLength * 60,
                };
                return periodCalculator[period.type] || 0;
            };

            self.readAll().then((data) => {
                // JAVÍTÁS (MEMÓRIA + BEFAGYÁS): ezek a sorok a MÁR RÖGZÍTETT
                // (történelmi) lapok/gólok/kiállítások "visszajátszására"
                // szolgálnak - arra, hogy a komponensek (Court, ActionBar,
                // PlayerList, PlayerPenalty, Clock) a felépülésük UTÁN
                // megkapják a korábbi eseményeket, és helyre tudják állítani a
                // saját belső állapotukat. Ez egy EGYSZERI, indulási
                // mechanizmus: a sorok kiürítése kizárólag a
                // `GAMEDATALOADED` (belépés / RESTOREDONE, l. App.vue
                // loadDataFromDB) vagy a `LOADQUEUE` eseményre történik.
                //
                // A `gamedata()` viszont a 10 másodperces periodikus resyncből
                // is lefut (App.vue periodicResyncInterval -> game/loadGameData
                // -> dbmanager.gamedata()), ami SOHA nem vált ki
                // `GAMEDATALOADED`-et. Így minden egyes ciklusban ÚJRA
                // felhalmozódott az ÖSSZES történelmi esemény egy-egy mély
                // másolata, ürítés nélkül: egy ~100 gól/lap eseményes, 60
                // perces mérkőzésnél ez kb. 360 ciklus × 100 bejegyzés ≈
                // 36 000 felhalmozott másolat (több tíz MB szemét) - ez maga a
                // fokozatos lassulás. Ráadásul amikor végre jött egy
                // reconnect (RESTOREDONE -> GAMEDATALOADED), az EGÉSZ
                // felhalmozott sor egyszerre, szinkron módon visszajátszódott,
                // eseményenként ~8 élő kezelőbe - ez okozta a másodpercektől
                // percekig tartó teljes befagyást.
                //
                // A megoldás: a feltöltés előtt ürítjük a sorokat, így azok
                // mindig PONTOSAN EGY, friss pillanatképet tartalmaznak a
                // történelemről - se felhalmozódás, se robbanásszerű
                // visszajátszás. A normál indulási útvonal változatlan: a
                // `gamedata()` feltölti, a `GAMEDATALOADED` kiüríti.
                self.eventqueue.length = 0;
                self.eventqueuemid.length = 0;
                self.eventqueuehigh.length = 0;

                let _gamedata = {};
                data.forEach(row => {
                    if (row.action === 'init') {
                        let team;
                        let callback = (person) => {
                            person.cards = {
                                yellow: { value: false, given: undefined },
                                red: { value: false, given: undefined },
                                blue: { value: false, given: undefined }
                            };
                            person.penalties = [];
                            person.strandYellows = [];
                            person.disabled = require('@/DefaultBlockList')();
                            person.team = team;
                        };
                        team = 'teamA';
                        [...row.params.team_a_players, ...row.params.team_a_officials].forEach(callback);
                        team = 'teamB';
                        [...row.params.team_b_players, ...row.params.team_b_officials].forEach(callback);
                        [...row.params.team_a_officials, ...row.params.team_b_officials].forEach((person) => {
                            person.official = true;
                            person.maxPenalties = 3;
                        });
                        [...row.params.team_a_players, ...row.params.team_b_players].forEach((person) => {
                            person.official = false;
                            person.maxPenalties = 3;
                        });
                        let previousPeriod = null;

                        // Sort periods chronologically before computing startat
                        let isStrand = row.params.rule === 'STRAND';
                        // JAVÍTÁS: itt korábban a `periodOrder.js` CHRONO_MAP-jának egy
                        // KÉZZEL BEMÁSOLT, negyedik példánya állt. Egy táblázat-javítást
                        // így minden példányban külön kellett elvégezni - pontosan ez
                        // okozta, hogy a 7m dobások sorrend-javítása egy ideig csak
                        // részlegesen érvényesült. Mostantól a közös, egyetlen
                        // igazság-forrásból (`@/utils/periodOrder`) származik.
                        const getChronoOrder = require('@/utils/periodOrder').getChronoOrder;
                        // A szerver minden periódushoz konkrét, hiteles "order" mezőt küld.
                        // Ha ez létezik és szám, azt használjuk; a `periodOrder.js`
                        // táblázata csak akkor marad tartalék, ha az "order" mező hiányzik.
                        row.params.periods.sort((a, b) => {
                            const orderA = parseInt(a.order);
                            const orderB = parseInt(b.order);
                            if (!isNaN(orderA) && !isNaN(orderB)) {
                                return orderA - orderB;
                            }
                            return getChronoOrder(a.value, isStrand) - getChronoOrder(b.value, isStrand);
                        });

                        row.params.periods.forEach((period) => {
                            period.duration = getPeriodDuration(period, row.params.period_length);
                            period.isDisabled = period.value === '14';
                            period.order = period.value === '13' ? 14 : period.value === '14' ? 13 : period.value;
                            period.previousValue = previousPeriod ? previousPeriod.value : null;
                            previousPeriod = period;
                        });

                        // A `startat` számítása a KÖZÖS `periodStartat.js`-ben történik.
                        // KORÁBBI HIBA: az itteni saját implementáció minden EGYES
                        // hosszabbítás-periódusnál külön megkövetelte, hogy legyen benne
                        // rögzített esemény - ha egy periódusváltás rögzítése kimaradt,
                        // az utána következő periódusok kezdő ideje elcsúszott. Ráadásul
                        // ez a függvény a 10 másodperces periodikus resync miatt
                        // folyamatosan újrafutott, és felülírta a `game.js` SET_PERIOD
                        // által (más logikával) beállított értéket - így az óra
                        // periódusváltás után rövid időn belül "visszaugrott".
                        const { applyPeriodStartAt, collectReachedPeriods } = require('@/utils/periodStartat');
                        applyPeriodStartAt(
                            row.params.periods,
                            collectReachedPeriods(data),
                            window.period
                        );
                        _gamedata = row;
                    }
                });
                // JAVÍTÁS (BEFAGYÁS): ha a helyi adatbázisban NINCS `init` rekord (vagy
                // van, de nincs rajta `params`), akkor `_gamedata` üres objektum marad -
                // az alábbi ciklusok viszont több helyen FELTÉTEL NÉLKÜL hivatkoznak a
                // `_gamedata.params`-ra:
                //   - a `gamedatachange` ág:  `_gamedata.params[row.params.name] = ...`
                //   - a `player_entry` ág:    `_gamedata.params.team_a_players.forEach(...)`
                // Ilyenkor `TypeError: undefined is not an object` keletkezik.
                //
                // Miért volt ez SÚLYOS: ez a függvény egy `new Promise`-ban fut, amiben
                // NINCS `reject` és NINCS `catch`. Egy dobás után tehát a promise SOHA nem
                // dől el - a hívó (`game.js` loadGameData) örökre vár rá, és mivel az
                // egyetlen `SET_OVERLAY(false)` a betöltés végén van, a felhasználó a
                // betöltő overlay mögött ragad: a felület "lefagy", és mivel a helyzet
                // determinisztikus, sem az oldal frissítése, sem a böngésző újraindítása
                // nem segít. Valós eset: 32021442489 (Martonvásár) - a játékvezető két
                // napig nem tudta lezárni a jegyzőkönyvet; Bugsnag: 50 előfordulás
                // 2026-08-27 óta, Safari 17.6 / macOS.
                //
                // A "nincs init" eset már eddig is LÉTEZŐ, KEZELT állapot volt - lentebb
                // `if (!Object.keys(_gamedata).length) { resolve(null); }` áll, és a hívó
                // a `null`-t helyesen kezeli (`if (!result) return false;`), majd a 10
                // másodperces periodikus resync újrapróbálja. A baj csak az volt, hogy ez
                // az ellenőrzés a két dereferencia UTÁN következett, tehát sosem jutottunk
                // el odáig. Itt, a ciklusok ELŐTT ugyanazt az - ismert és biztonságos -
                // kimenetet adjuk vissza, csak időben.
                //
                // Mikor fordul elő? Az `init` rekordot a szerver restore-ja SOHA nem küldi
                // (l. getRoomData: `if (row.action != "init")`), azt a kliens írja magának
                // a `gamedataCallback()`-ben. A restore viszont közben már behozza a
                // `gamedatachange` / `player_entry` rekordokat - vagyis van egy ablak,
                // amiben ezek már megvannak, az `init` még nem.
                if (!_gamedata.params) {
                    debug("gamedata(): nincs init rekord a helyi adatbázisban - kihagyva, a következő resync újrapróbálja");
                    resolve(null);
                    return;
                }

                // Az ERP-től (a verseny szintjéből) származó, KÖTELEZŐ jegyzőkönyv-típus
                // megőrzése, MIELŐTT az alábbi `gamedatachange` visszajátszás felülírná a
                // `params.basic`-ot. A `params.basic` innentől az ÉPPEN ÉRVÉNYES nézetet
                // jelenti (amit a jegyzőkönyvvezető a Beállításokban állíthatott), az
                // `erpBasic` pedig azt, amit a verseny szintje ELŐÍR. A kettő
                // szétválasztása nélkül nem lehet megmondani, hogy egy `basic: true`
                // szabályos választás-e (alacsonyabb osztály) vagy szabálytalan
                // visszaváltás (NB I) - l. a Settings.vue `isExtendedMandated`-jét.
                if (_gamedata.params) {
                    _gamedata.params.erpBasic = _gamedata.params.basic;
                }

                _gamedata.timelineData = [];
                _gamedata.substituteTimeline = [];
                _gamedata.dmrTimeline = [];
                _gamedata.dmrPlayers = [];
                const dmrInitRows = [];

                let last = {};
                let periodsLoaded = [];
                last.period = '1';
                last.date = 0;
                last.gametime = 0;

                data.forEach(row => {
                    let lastPeriod = parseInt(last.period);
                    let rowPeriod = parseInt(row.period);
                    // JAVÍTÁS: bizonyos rekordoknak (pl. 'periodchange') nincs gametime
                    // mezőjük - ha ezt nyers undefined-ként írnánk a last.gametime-ba, a
                    // KÖVETKEZŐ összehasonlítás (last.gametime < row.gametime) örökre
                    // false lenne (undefined < szám mindig false), így a last.gametime
                    // véglegesen "beragadt" volna, akkor is, ha később ugyanabban a
                    // periódusban valódi, nagyobb gametime-ú esemény történt. Ez okozta,
                    // hogy RC/reconnect után a stopper a félidő ELEJÉRE (0 relatív idő)
                    // állt vissza, ahelyett hogy az utolsó tényleges eseményhez ugrott
                    // volna.
                    const rowGametime = row.gametime || 0;
                    if (lastPeriod < rowPeriod) {
                        last.date = row.date;
                        last.gametime = rowGametime;
                        last.period = row.period;
                    }
                    if (lastPeriod === rowPeriod && (last.gametime || 0) < rowGametime) {
                        last.date = row.date;
                        last.gametime = rowGametime;
                        last.period = row.period;
                    }
                    if (row.action === 'gamedatachange') {
                        if (typeof row.params.value !== "undefined" && typeof row.params.value.id !== "undefined") {
                            _gamedata.params[row.params.name] = { id: row.params.value.id };
                        }
                        else if(row.params.name === "basic" && typeof row.params.value !== "undefined") {
                            _gamedata.params[row.params.name] = row.params.value;
                        }
                    }

                    if (CONSTANTS.TIMELINEACTIONS.REPORT.includes(row.action) || (row.params && row.params.isDraft) ) {
                        let data = {};
                        data.action = row.action;
                        data.id = row.UUID;
                        data.UUID = row.UUID;
                        data.isDraft = row.params.isDraft;
                        data.params = row.params;
                        data.gametime = row.gametime;
                        data.period = row.period;
                        data.clientTime = row.clientTime;
                        data.deleted = row.deleted;
                        data.synced = row.synced;
                        data.syncDone = row.syncDone;
                        _gamedata.timelineData.push(data);
                    }

                    if (['piroslap', 'keklap', 'sargalap'].includes(row.action) && !row.deleted) {
                        addToMidQueue(CONSTANTS.EVENTS.RECORD.CREATED, {
                            action: row.action,
                            params: row.params,
                            result: row.UUID,
                            syncDone: row.syncDone,
                            synced: row.synced,
                            gameTime: row.gameTime,
                            totalGameTime: row.totalGameTime,
                            isHistorical: true,
                        });
                    }

                    if (['2perc', '4perc', 'kiallitas', 'gol', 'gol7m', 'gol2p', 'gol6m'].includes(row.action) && !row.deleted) {
                        addToQueue(CONSTANTS.EVENTS.RECORD.CREATED, {
                            action: row.action,
                            params: row.params,
                            result: row.UUID,
                            syncDone: row.syncDone,
                            synced: row.synced,
                            gameTime: row.gameTime,
                            totalGameTime: row.totalGameTime,
                            isHistorical: true,
                        });
                    }
                });

                data.forEach(row => {
                    if (window.usertype === "sub" && CONSTANTS.TIMELINEACTIONS.SUBSTITUTE.includes(row.action)) {
                        let data = {};
                        data.action = row.action;
                        data.id = row.UUID;
                        data.isDraft = false;
                        data.params = row.params;
                        data.synced = row.synced;
                        data.syncDone = row.syncDone;
                        _gamedata.substituteTimeline.push(row);
                    }

                    if(window.usertype === "dmr") {
                        // JAVÍTÁS (80. tétel): az 'init' rekordokat itt csak
                        // összegyűjtjük, a játékoslista a ciklus után, duplikáció
                        // nélkül épül fel (l. lentebb).
                        if(row.action === 'init') {
                            dmrInitRows.push(row);
                        }
                        if(row.action === 'periodchange') {
                            _gamedata.dmrTimeline.push(row);
                        }
                        if([...CONSTANTS.DMR.DMR_DRAFTS, ...CONSTANTS.DMR.APPROVE_REQUIRED].includes(row.action)) {
                            _gamedata.dmrTimeline.push(row);
                        }
                    }

                    if (!periodsLoaded.includes(row.period)) {
                        periodsLoaded.push(row.period);
                    }
                });

                // JAVÍTÁS (80. tétel - 32021426449): egy meccshez több 'init' rekord
                // is tartozhat (minden eszköz az első belépésekor írt egyet saját,
                // véletlen UUID-dal; a DMR élőben megkapta a CR-ét is). Korábban
                // MINDEN 'init' teljes kerete bekerült a listába, így két 'init'
                // esetén minden játékos kétszer szerepelt, és a DMR "Összesítés"
                // füle minden sort - a gólokat, büntetéseket és az összesítő sort is -
                // duplán mutatta. Most egy játékos (csapat + azonosító + játékos/
                // hivatalos) egyszer szerepel, az időben legfrissebb 'init'
                // adataival, az első előfordulás helyén.
                if (window.usertype === "dmr" && dmrInitRows.length) {
                    const dmrPlayerMap = new Map();
                    const addDmrPlayers = function (list, team, kind) {
                        (list || []).forEach(function (player) {
                            if (!player) return;
                            dmrPlayerMap.set(team + '|' + kind + '|' + player.id, { id: player.id, team: team, jersey: player.jersey, name: player.name });
                        });
                    };
                    [...dmrInitRows]
                        .sort((a, b) => (Number(a.clientTime) || 0) - (Number(b.clientTime) || 0))
                        .forEach(function (initRow) {
                            const p = initRow.params || {};
                            addDmrPlayers(p.team_a_players, 'A', 'p');
                            addDmrPlayers(p.team_a_players_entry, 'A', 'p');
                            addDmrPlayers(p.team_a_officials, 'A', 'o');
                            addDmrPlayers(p.team_b_players, 'B', 'p');
                            addDmrPlayers(p.team_b_players_entry, 'B', 'p');
                            addDmrPlayers(p.team_b_officials, 'B', 'o');
                        });
                    _gamedata.dmrPlayers = Array.from(dmrPlayerMap.values());
                }

                if(typeof last.gametime === 'undefined' || last.gametime === null) {
                    last.gametime = 0;
                }

                _gamedata.timelineData.sort(function (a, b) {
                    if (a.params.gameTime > b.params.gameTime) {
                        return 1;
                    }
                    return -1;
                });

                if (!_gamedata.timelineData.length) { delete _gamedata.timelineData; }
                if (!_gamedata.substituteTimeline.length) { delete _gamedata.substituteTimeline; }
                if (!_gamedata.dmrTimeline.length) { delete _gamedata.dmrTimeline; }
                if (!_gamedata.dmrPlayers.length) { delete _gamedata.dmrPlayers; }
                if (!Object.keys(_gamedata).length) { resolve(null); }

                // JAVÍTÁS: az 'init' rekord SAJÁT period mezője örökre "1" marad (soha nem
                // frissül), a fenti ciklus viszont helyesen kiszámolja `last.period`-ban a
                // ténylegesen legutolsó (legmagasabb) periódust, amiben esemény történt.
                // Enélkül a loadGameData() minden reconnect/resync alkalmával VISSZAÁLLÍTOTTA
                // a state.currentPeriod-ot (és vele window.period-ot) "1"-re, még akkor is,
                // ha a mérkőzés már a II. félidőben (vagy azon túl) járt - ez okozta, hogy
                // DC/RC teszt után a 2. félidőben rögzített események összekeveredtek az
                // 1. félidős időszámítással.
                _gamedata.period = last.period;

                addToHighQueue(CONSTANTS.EVENTS.LASTTIMELOAD, {
                    last: last,
                    periods: periodsLoaded.sort(),
                });

                let subEnteredPlayer = [];
                let reportEnteredPlayer = [];
                data.forEach(row => {
                    if (row.action === CONSTANTS.ACTIONS.PLAYERENTRY) {
                        if(row.params.playerEntryScreen === 'substitute') {
                            let m_player_id = row.params.player.id;
                            let playerfound = false;
                            _gamedata.params.team_a_players.forEach(element => { if(element.id === m_player_id){ playerfound = true; } });
                            _gamedata.params.team_b_players.forEach(element => { if(element.id === m_player_id){ playerfound = true; } });
                            if(!playerfound){ subEnteredPlayer.push(row); }
                        }
                        if(row.params.playerEntryScreen === 'report') {
                            let m_player_id = row.params.player.id;
                            let playerfound = false;
                            _gamedata.params.team_a_players.forEach(element => { if(element.id === m_player_id){ playerfound = true; } });
                            _gamedata.params.team_b_players.forEach(element => { if(element.id === m_player_id){ playerfound = true; } });
                            if(!playerfound){ reportEnteredPlayer.push(row); }
                        }
                    }
                });

                if(subEnteredPlayer.length && !reportEnteredPlayer.length) {
                    subEnteredPlayer.forEach(function(row) {
                        let team = row.params.player.team.slice(-1);
                        if (team === 'A') {
                            _gamedata.params.team_a_players.push(row.params.player);
                            for (let i = 0; i < _gamedata.params.team_a_players_entry.length; i++) {
                                if (row.params.player.id === _gamedata.params.team_a_players_entry[i].id) {
                                    _gamedata.params.team_a_players_entry.splice(i, 1);
                                }
                            }
                        }
                        if (team === 'B') {
                            _gamedata.params.team_b_players.push(row.params.player);
                            for (let i = 0; i < _gamedata.params.team_b_players_entry.length; i++) {
                                if (row.params.player.id === _gamedata.params.team_b_players_entry[i].id) {
                                    _gamedata.params.team_b_players_entry.splice(i, 1);
                                }
                            }
                        }
                    });
                }

                if(reportEnteredPlayer.length) {
                    reportEnteredPlayer.forEach(function(row) {
                        let team = row.params.player.team.slice(-1);
                        if (team === 'A') {
                            _gamedata.params.team_a_players.push(row.params.player);
                            for (let i = 0; i < _gamedata.params.team_a_players_entry.length; i++) {
                                if (row.params.player.id === _gamedata.params.team_a_players_entry[i].id) {
                                    _gamedata.params.team_a_players_entry.splice(i, 1);
                                }
                            }
                        }
                        if (team === 'B') {
                            _gamedata.params.team_b_players.push(row.params.player);
                            for (let i = 0; i < _gamedata.params.team_b_players_entry.length; i++) {
                                if (row.params.player.id === _gamedata.params.team_b_players_entry[i].id) {
                                    _gamedata.params.team_b_players_entry.splice(i, 1);
                                }
                            }
                        }
                    });

                    if(subEnteredPlayer.length) {
                        let foundIndex = null;
                        subEnteredPlayer.forEach(function(player) {
                            foundIndex = _gamedata.params.team_a_players.findIndex(subPlayer => subPlayer.id === player.params.id);
                            if(foundIndex !== -1) {
                                _gamedata.params.team_a_players.splice(foundIndex, 1);
                                _gamedata.params.team_a_players_entry.push(player);
                            }

                            foundIndex = _gamedata.params.team_b_players.findIndex(subPlayer => subPlayer.id === player.params.id);
                            if(foundIndex !== -1) {
                                _gamedata.params.team_b_players.splice(foundIndex, 1);
                                _gamedata.params.team_b_players.push(player);
                            }
                        });
                    }
                }

                resolve(JSON.parse(JSON.stringify(_gamedata)));
            }).catch((error) => {
                // BIZTONSÁGI HÁLÓ: a fenti korai kilépés a MA ISMERT dereferenciákat
                // (gamedatachange, player_entry) lefedi, de ez a függvény hosszú és
                // sok helyen nyúl a rekordok mezőihez. Egy jövőbeli, váratlan dobás
                // ugyanahhoz a tünethez vezetne: a promise nem dőlne el, a hívó
                // (`loadGameData`) örökre várna, és a felület a betöltő overlay mögött
                // ragadna. A `null` a hívónál MÁR MA IS kezelt kimenet
                // (`if (!result) return false;`), és a 10 másodperces periodikus
                // resync automatikusan újrapróbálja - tehát a legrosszabb eset egy
                // kihagyott ciklus, nem pedig végleges befagyás.
                console.error("gamedata() failed:", error);
                Bugsnag.notify(error instanceof Error ? error : new Error("gamedata() failed: " + error), function (event) {
                    event.severity = 'error';
                    event.context = 'dbmanager.gamedata()';
                    event.addMetadata('match', { merkozesId: window.dbname, usertype: window.usertype });
                });
                resolve(null);
            });
        }
    );
};

dbmanager.prototype.generateUUID = function () {
    var d = new Date().getTime();
    var d2 = (performance && performance.now && (performance.now() * 1000)) || 0;
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16;
        if (d > 0) {
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
        } else {
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
};

dbmanager.prototype.setConfig = function (value) {
    return new Promise(function (resolve, reject) {
        try {
            localStorage.setItem(value.name, JSON.stringify(value));
            resolve(value.name);
        } catch (err) {
            reject(err);
        }
    });
};

dbmanager.prototype.getConfig = function (key) {
    return new Promise(function (resolve, reject) {
        try {
            const item = localStorage.getItem(key);
            if (item) {
                resolve(JSON.parse(item));
            } else {
                resolve(null);
            }
        } catch (err) {
            reject(err);
        }
    });
};

export default new dbmanager();
